import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Menu, { IMenu, TMenuProps } from "../Menu/Menu";
import TextInput, { ITextInput, TInputElement, TTextInputProps } from "./TextInput";
import { useOverlay } from "../Overlay/OverlayProvider";
import { TAnchorProps } from "../Overlay/AnchoredBackdrop";
import { UpdateEvent } from "../GUIEvents";

export type TAutoCompleteProps = TTextInputProps & {
    menuProps?:Partial<TMenuProps<string>>
    anchorProps?:Partial<TAnchorProps>
}

export interface IAutoComplete extends ITextInput {

}

var g_id = 0
export default forwardRef((props:TAutoCompleteProps, ref?:ForwardedRef<IAutoComplete>) => {
    const {menuProps,onFocus,onBlur,onUpdate,value:_value,defaultValue:_defaultValue,...inputProps} = props

    const isManaged = props.hasOwnProperty("value")
    const [value,setValue] = useState<string>(_value ?? _defaultValue ?? "")
    const current = isManaged ? props.value! : value

    const [open,setOpen] = useState<boolean>(false)
    const inputRef = useRef<ITextInput|null>(null)
    const menuId = useRef<string>(`autocomplete_${g_id++}`)
    const menuRef = useRef<IMenu<string>|null>(null)
    const {addOverlay,popOverlay} = useOverlay()

    useImperativeHandle(ref, () => ({...inputRef.current!}))

    // if clicked on in put, don't dismiss
    const canDismiss = (e:PointerEvent) => inputRef.current?.input != document.elementFromPoint(e.clientX, e.clientY)
    const onSelect = (e:UpdateEvent<string,HTMLDivElement>) => {
        if (!inputRef.current || e.value===undefined || e.value===null) return

        if (!isManaged) setValue(e.value)
        inputRef.current.input?.blur()
        popOverlay(menuId.current)
        setOpen(false)
        onUpdate?.(new UpdateEvent(e.nativeEvent, e.value, inputRef.current?.input))
    }
    const _onFocus = (e:React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(e)
        const menu = <Menu options={props.menuProps?.options||[]} onUpdate={onSelect} ref={menuRef} filter={current}/>
        const anchor = {follow:menuId.current, ...props.anchorProps}
        addOverlay(menuId.current, {children:menu,anchor,canDismiss})
        setOpen(true)
    }
    const _onBlur = (e:React.FocusEvent<HTMLInputElement>) => {
        onBlur?.(e)
        popOverlay(menuId.current)
        setOpen(false)
    }
    const _onUpdate = (e:UpdateEvent<string,TInputElement>) => {
        if (!isManaged) setValue(e.value || "")
        onUpdate?.(e)
        menuRef.current?.filter(e.value || "")
    }
    useEffect(() => {
        inputRef.current?.setAttribute("follow-id", menuId.current)
        return () => { open && popOverlay(menuId.current) }
    }, [])

    return <TextInput {...inputProps} value={current} onFocus={_onFocus} onBlur={_onBlur} onUpdate={_onUpdate} ref={inputRef}/>
})